import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useSportsLang } from '../contexts/SportsLangContext';
import { VUKZ, VUKZ_GRADIENT } from '../theme/vukzTheme';
import PageHeader from '../components/layout/PageHeader';
import { getRankings, getEvents, getCountries } from '../services/swimmingService';
import {
  Trophy, ChevronDown, User, Building2, Timer, Medal,
  Globe, Flag, Filter, Search
} from 'lucide-react';

const LABELS = {
  pt: {
    pageTitle: 'Rankings Natação',
    pageSubtitle: `Melhores tempos por prova · Dados de ${[2026,2025,2024,2023].length} temporadas`,
    allYears: 'Todos os anos',
    loadingEvents: 'Carregando...',
    selectEvent: 'Selecione a prova',
    selectCountry: 'Selecione o país',
    widget1Title: 'Ranking VUKZ',
    widget2Title: 'Ranking por País',
    widget2SubtitleEmpty: 'Selecione um país para comparar',
    genderM: '♂ Masc',
    genderF: '♀ Fem',
    tableHeaders: ['#', 'Atleta', 'Clube', 'Tempo', 'Torneio', 'Ano'],
    emptyDefault: 'Nenhum resultado encontrado',
    emptySelectEvent: 'Selecione uma prova para ver o ranking',
    emptySelectCountryEvent: 'Selecione um país e uma prova',
    emptySelectEvent2: 'Selecione uma prova',
    emptyNoResults: 'Nenhum resultado encontrado para este filtro',
  },
  en: {
    pageTitle: 'Swimming Rankings',
    pageSubtitle: `Best times by event · Data from ${[2026,2025,2024,2023].length} seasons`,
    allYears: 'All years',
    loadingEvents: 'Loading...',
    selectEvent: 'Select event',
    selectCountry: 'Select country',
    widget1Title: 'VUKZ Ranking',
    widget2Title: 'Country Ranking',
    widget2SubtitleEmpty: 'Select a country to compare',
    genderM: '♂ Male',
    genderF: '♀ Female',
    tableHeaders: ['#', 'Athlete', 'Club', 'Time', 'Meet', 'Year'],
    emptyDefault: 'No results found',
    emptySelectEvent: 'Select an event to view the ranking',
    emptySelectCountryEvent: 'Select a country and an event',
    emptySelectEvent2: 'Select an event',
    emptyNoResults: 'No results found for this filter',
  }
};

/* ═══════════════════════════════════════════════════════════════
   SwimmingRankingsPage — VUKZ Swimming Rankings
   Route: /natacao/rankings
   Two widgets:
     1. Ranking VUKZ — global best times by event, gender, year
     2. Ranking por País — best times filtered by country
   ═══════════════════════════════════════════════════════════════ */

// ── Helpers ──────────────────────────────────────────────────

const AVAILABLE_YEARS = [2026, 2025, 2024, 2023];
const CURRENT_YEAR = AVAILABLE_YEARS[0]; // Use latest year with data

/** Normalize stroke names (backend has "Livre", "LIVRE", "NADO LIVRE", etc.) */
const normalizeStroke = (s) => {
  if (!s) return '';
  const upper = s.toUpperCase().trim();
  if (upper === 'NADO LIVRE' || upper === 'LIVRE') return 'Livre';
  if (upper === 'COSTAS') return 'Costas';
  if (upper === 'PEITO') return 'Peito';
  if (upper === 'BORBOLETA') return 'Borboleta';
  if (upper === 'MEDLEY') return 'Medley';
  return s;
};

/** Build a display name like "50m Livre" */
const eventDisplayName = (distance, stroke) =>
  `${distance}m ${normalizeStroke(stroke)}`;

/** Deduplicate events by distance+stroke+gender, keep first ID */
const deduplicateEvents = (events) => {
  const map = new Map();
  for (const e of events) {
    const key = `${e.distance_meters}-${normalizeStroke(e.stroke)}-${e.gender}`;
    if (!map.has(key)) {
      map.set(key, {
        ...e,
        displayName: eventDisplayName(e.distance_meters, e.stroke),
        normalizedStroke: normalizeStroke(e.stroke),
      });
    }
  }
  return Array.from(map.values()).sort(
    (a, b) => a.distance_meters - b.distance_meters || a.normalizedStroke.localeCompare(b.normalizedStroke)
  );
};

/** Format country code for display */
const countryLabel = (c) => {
  const names = {
    BRA: '🇧🇷 Brasil', USA: '🇺🇸 EUA', AUS: '🇦🇺 Austrália',
    GBR: '🇬🇧 Grã-Bretanha', CHN: '🇨🇳 China', JPN: '🇯🇵 Japão',
    ITA: '🇮🇹 Itália', GER: '🇩🇪 Alemanha', FRA: '🇫🇷 França',
    HUN: '🇭🇺 Hungria', CAN: '🇨🇦 Canadá', NED: '🇳🇱 Holanda',
    SWE: '🇸🇪 Suécia', RSA: '🇿🇦 África do Sul', KOR: '🇰🇷 Coreia do Sul',
    ESP: '🇪🇸 Espanha', ROU: '🇷🇴 Romênia',
    Australia: '🇦🇺 Austrália', Brasil: '🇧🇷 Brasil',
  };
  return names[c] || c;
};

// ── Reusable Components ──────────────────────────────────────

const SelectPill = ({ value, onChange, options, icon: Icon, placeholder }) => (
  <div className="relative">
    <div className="flex items-center gap-1.5 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
      style={{ color: VUKZ.textLight }}>
      {Icon && <Icon size={13} />}
    </div>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="appearance-none pl-8 pr-8 py-2 rounded-xl text-xs font-bold border bg-white cursor-pointer hover:border-gray-300 focus:outline-none focus:ring-2 transition-all"
      style={{ borderColor: VUKZ.border, color: VUKZ.text, focusRingColor: VUKZ.primary }}
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
    <ChevronDown size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none"
      style={{ color: VUKZ.textLight }} />
  </div>
);

const GenderToggle = ({ value, onChange }) => {
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  return (
  <div className="flex rounded-xl border overflow-hidden" style={{ borderColor: VUKZ.border }}>
    {[
      { key: 'M', label: t.genderM },
      { key: 'F', label: t.genderF },
    ].map((g) => (
      <button
        key={g.key}
        onClick={() => onChange(g.key)}
        className="px-4 py-2 text-xs font-bold transition-all"
        style={{
          backgroundColor: value === g.key ? VUKZ.primary : 'white',
          color: value === g.key ? 'white' : VUKZ.textLight,
        }}
      >
        {g.label}
      </button>
    ))}
  </div>
  );
};

const RankingTable = ({ rankings, loading, emptyMessage }) => {
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  if (loading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="h-12 rounded-xl animate-pulse" style={{ backgroundColor: VUKZ.primaryLight }} />
        ))}
      </div>
    );
  }

  if (!rankings || rankings.length === 0) {
    return (
      <div className="text-center py-10">
        <div className="w-12 h-12 rounded-2xl mx-auto mb-3 flex items-center justify-center"
          style={{ backgroundColor: VUKZ.primaryLight }}>
          <Search size={20} style={{ color: VUKZ.textLight }} />
        </div>
        <p className="text-xs font-medium" style={{ color: VUKZ.textLight }}>
          {emptyMessage || t.emptyDefault}
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto -mx-5">
      <table className="w-full min-w-[600px]">
        <thead>
          <tr className="border-b" style={{ borderColor: VUKZ.border }}>
            {t.tableHeaders.map((h) => (
              <th key={h} className="text-left px-5 py-2.5 text-[10px] font-bold uppercase tracking-wider"
                style={{ color: VUKZ.textLight }}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rankings.map((r, idx) => {
            const pos = r.rank_position || idx + 1;
            const isMedal = pos <= 3;
            const medalColors = ['#FFD700', '#C0C0C0', '#CD7F32'];

            return (
              <tr
                key={`${r.swimmer_id}-${idx}`}
                className="border-b hover:bg-gray-50/50 transition-colors"
                style={{ borderColor: VUKZ.border }}
              >
                {/* Position */}
                <td className="px-5 py-3">
                  {isMedal ? (
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${medalColors[pos - 1]}20`, color: medalColors[pos - 1] }}>
                      <Medal size={14} />
                    </div>
                  ) : (
                    <span className="text-xs font-extrabold" style={{ color: VUKZ.textLight }}>
                      {pos}
                    </span>
                  )}
                </td>

                {/* Athlete */}
                <td className="px-5 py-3">
                  <Link
                    to={`/natacao/nadador/${r.swimmer_id}`}
                    className="flex items-center gap-2 group"
                  >
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: VUKZ.primaryLight }}>
                      <User size={14} style={{ color: VUKZ.primary }} />
                    </div>
                    <div>
                      <p className="text-xs font-bold group-hover:underline" style={{ color: VUKZ.text }}>
                        {r.swimmer_name}
                      </p>
                      {r.nationality && (
                        <p className="text-[10px]" style={{ color: VUKZ.textLight }}>
                          {r.nationality}
                        </p>
                      )}
                    </div>
                  </Link>
                </td>

                {/* Club */}
                <td className="px-5 py-3">
                  <div className="flex items-center gap-1.5">
                    <Building2 size={12} style={{ color: VUKZ.textLight }} />
                    <span className="text-xs" style={{ color: VUKZ.text }}>
                      {r.club_name || '—'}
                    </span>
                  </div>
                </td>

                {/* Time */}
                <td className="px-5 py-3">
                  <span className="text-xs font-extrabold font-mono" style={{ color: VUKZ.primary }}>
                    {r.time_formatted || (r.best_time ? parseFloat(r.best_time).toFixed(2) : '—')}
                  </span>
                </td>

                {/* Competition */}
                <td className="px-5 py-3">
                  <span className="text-xs" style={{ color: VUKZ.textLight }}>
                    {r.competition_name || '—'}
                  </span>
                </td>

                {/* Year */}
                <td className="px-5 py-3">
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-md"
                    style={{ backgroundColor: VUKZ.primaryLight, color: VUKZ.primary }}>
                    {r.competition_year || '—'}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ── Widget Card Wrapper ──────────────────────────────────────

const WidgetCard = ({ icon: Icon, title, subtitle, filters, children }) => (
  <div className="bg-white rounded-2xl border overflow-hidden" style={{ borderColor: VUKZ.border }}>
    {/* Widget header */}
    <div className="px-5 pt-5 pb-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ background: VUKZ_GRADIENT }}>
            <Icon size={18} color="white" />
          </div>
          <div>
            <h2 className="text-sm font-extrabold" style={{ color: VUKZ.text }}>{title}</h2>
            {subtitle && (
              <p className="text-[10px]" style={{ color: VUKZ.textLight }}>{subtitle}</p>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      {filters && (
        <div className="flex flex-wrap items-center gap-2">
          {filters}
        </div>
      )}
    </div>

    {/* Content */}
    <div className="px-5 pb-5">
      {children}
    </div>
  </div>
);

// ── Main Page ────────────────────────────────────────────────

const SwimmingRankingsPage = () => {
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  // Shared state
  const [allEvents, setAllEvents] = useState([]);
  const [countries, setCountries] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);

  // Widget 1: Ranking VUKZ (Global)
  const [globalGender, setGlobalGender] = useState('M');
  const [globalYear, setGlobalYear] = useState(String(CURRENT_YEAR));
  const [globalEventId, setGlobalEventId] = useState('');
  const [globalRankings, setGlobalRankings] = useState([]);
  const [globalLoading, setGlobalLoading] = useState(false);

  // Widget 2: Ranking por País
  const [countryGender, setCountryGender] = useState('M');
  const [countryYear, setCountryYear] = useState(String(CURRENT_YEAR));
  const [countryCode, setCountryCode] = useState('');
  const [countryEventId, setCountryEventId] = useState('');
  const [countryRankings, setCountryRankings] = useState([]);
  const [countryLoading, setCountryLoading] = useState(false);

  // ── Load events + countries on mount ──
  useEffect(() => {
    const load = async () => {
      setEventsLoading(true);
      try {
        const [eventsRes, countriesRes] = await Promise.all([
          getEvents({ is_relay: 0 }),
          getCountries(),
        ]);
        const raw = eventsRes?.events || eventsRes?.items || eventsRes || [];
        setAllEvents(raw);

        const ctrs = countriesRes?.countries || countriesRes?.items || countriesRes || [];
        // Sort by total_athletes desc, filter those with athletes
        const sorted = ctrs
          .filter((c) => c.total_athletes > 0)
          .sort((a, b) => (b.total_athletes || 0) - (a.total_athletes || 0));
        setCountries(sorted);
      } catch (err) {
        console.error('Erro ao carregar eventos/países:', err);
      } finally {
        setEventsLoading(false);
      }
    };
    load();
  }, []);

  // ── Filtered events per gender ──
  const globalFilteredEvents = deduplicateEvents(
    allEvents.filter((e) => e.gender === globalGender)
  );
  const countryFilteredEvents = deduplicateEvents(
    allEvents.filter((e) => e.gender === countryGender)
  );

  // Auto-select first event when gender changes
  useEffect(() => {
    if (globalFilteredEvents.length > 0 && !globalEventId) {
      setGlobalEventId(String(globalFilteredEvents[0].id));
    }
  }, [globalFilteredEvents, globalEventId]);

  useEffect(() => {
    if (countryFilteredEvents.length > 0 && !countryEventId) {
      setCountryEventId(String(countryFilteredEvents[0].id));
    }
  }, [countryFilteredEvents, countryEventId]);

  // Reset event selection when gender changes
  const handleGlobalGenderChange = (g) => {
    setGlobalGender(g);
    setGlobalEventId(''); // will auto-select via useEffect
  };

  const handleCountryGenderChange = (g) => {
    setCountryGender(g);
    setCountryEventId('');
  };

  // ── Fetch global rankings ──
  const fetchGlobalRankings = useCallback(async () => {
    if (!globalEventId) return;
    setGlobalLoading(true);
    try {
      const res = await getRankings(
        Number(globalEventId),
        globalYear ? Number(globalYear) : null,
        50
      );
      setGlobalRankings(res?.rankings || []);
    } catch (err) {
      console.error('Erro ao buscar ranking global:', err);
      setGlobalRankings([]);
    } finally {
      setGlobalLoading(false);
    }
  }, [globalEventId, globalYear]);

  useEffect(() => {
    fetchGlobalRankings();
  }, [fetchGlobalRankings]);

  // ── Fetch country rankings ──
  const fetchCountryRankings = useCallback(async () => {
    if (!countryEventId || !countryCode) return;
    setCountryLoading(true);
    try {
      const res = await getRankings(
        Number(countryEventId),
        countryYear ? Number(countryYear) : null,
        50,
        countryCode
      );
      setCountryRankings(res?.rankings || []);
    } catch (err) {
      console.error('Erro ao buscar ranking por país:', err);
      setCountryRankings([]);
    } finally {
      setCountryLoading(false);
    }
  }, [countryEventId, countryYear, countryCode]);

  useEffect(() => {
    fetchCountryRankings();
  }, [fetchCountryRankings]);

  // ── Event label for current selection ──
  const getEventLabel = (eventId, events) => {
    const ev = events.find((e) => String(e.id) === String(eventId));
    return ev ? ev.displayName : '';
  };

  // ── Render ──
  return (
    <div className="min-h-screen" style={{ backgroundColor: VUKZ.bg }}>
      <div className="max-w-6xl mx-auto px-4 lg:px-6 py-6">

        {/* ═══ Page Header ═══ */}
        <PageHeader
          title={t.pageTitle}
          subtitle={t.pageSubtitle}
          badgeText="NATAÇÃO"
          className="mb-6"
        />

        <div className="space-y-6">

          {/* ═══ Widget 1: Ranking VUKZ (Global) ═══ */}
          <WidgetCard
            icon={Trophy}
            title={t.widget1Title}
            subtitle={`Top tempos ${getEventLabel(globalEventId, globalFilteredEvents) || 'todas as provas'} · ${globalYear || t.allYears}`}
            filters={
              <>
                <GenderToggle value={globalGender} onChange={handleGlobalGenderChange} />

                <SelectPill
                  value={globalYear}
                  onChange={setGlobalYear}
                  icon={Timer}
                  options={[
                    { value: '', label: t.allYears },
                    ...AVAILABLE_YEARS.map((y) => ({ value: String(y), label: String(y) })),
                  ]}
                />

                <SelectPill
                  value={globalEventId}
                  onChange={setGlobalEventId}
                  icon={Filter}
                  placeholder={eventsLoading ? t.loadingEvents : t.selectEvent}
                  options={globalFilteredEvents.map((e) => ({
                    value: String(e.id),
                    label: e.displayName,
                  }))}
                />
              </>
            }
          >
            <RankingTable
              rankings={globalRankings}
              loading={globalLoading || eventsLoading}
              emptyMessage={t.emptySelectEvent}
            />
          </WidgetCard>

          {/* ═══ Widget 2: Ranking por País ═══ */}
          <WidgetCard
            icon={Flag}
            title={t.widget2Title}
            subtitle={countryCode ? `${countryLabel(countryCode)} · ${getEventLabel(countryEventId, countryFilteredEvents) || '—'}` : t.widget2SubtitleEmpty}
            filters={
              <>
                <GenderToggle value={countryGender} onChange={handleCountryGenderChange} />

                <SelectPill
                  value={countryYear}
                  onChange={setCountryYear}
                  icon={Timer}
                  options={[
                    { value: '', label: t.allYears },
                    ...AVAILABLE_YEARS.map((y) => ({ value: String(y), label: String(y) })),
                  ]}
                />

                <SelectPill
                  value={countryCode}
                  onChange={setCountryCode}
                  icon={Globe}
                  placeholder={t.selectCountry}
                  options={countries.map((c) => ({
                    value: c.code || c.name,
                    label: `${countryLabel(c.code || c.name)} (${c.total_athletes})`,
                  }))}
                />

                <SelectPill
                  value={countryEventId}
                  onChange={setCountryEventId}
                  icon={Filter}
                  placeholder={eventsLoading ? t.loadingEvents : t.selectEvent}
                  options={countryFilteredEvents.map((e) => ({
                    value: String(e.id),
                    label: e.displayName,
                  }))}
                />
              </>
            }
          >
            <RankingTable
              rankings={countryRankings}
              loading={countryLoading}
              emptyMessage={
                !countryCode
                  ? t.emptySelectCountryEvent
                  : !countryEventId
                  ? t.emptySelectEvent2
                  : t.emptyNoResults
              }
            />
          </WidgetCard>

        </div>
      </div>
    </div>
  );
};

export default SwimmingRankingsPage;
